import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { CmtsErrorEnvelope, CmtsApiErrorKind } from "@cmts/api/cmtsApiErrors.js";

type ToastTone = "success" | "info" | "warning" | "error";

export type CmtsApiErrorSnapshot = {
  readonly kind: CmtsApiErrorKind;
  readonly status: number;
  readonly message: string;
  readonly code?: string | undefined;
  readonly correlationId?: string | undefined;
  readonly retryable: boolean;
  readonly atUtc: string;
};

export type CmtsToast = { readonly id: string; readonly tone: ToastTone; readonly message: string; readonly atUtc: string; readonly correlationId?: string | undefined };
export type CmtsPermissionBlockUiState = { readonly permission?: string | undefined; readonly route?: string | undefined; readonly message: string; readonly atUtc: string; readonly correlationId?: string | undefined };

export type CmtsUiState = {
  readonly globalLoading: boolean;
  readonly sidebarCollapsed: boolean;
  readonly lastCorrelationId?: string | undefined;
  readonly lastError?: CmtsApiErrorSnapshot | undefined;
  readonly permissionBlock?: CmtsPermissionBlockUiState | undefined;
  readonly toasts: readonly CmtsToast[];
};

const initialState: CmtsUiState = {
  globalLoading: false,
  sidebarCollapsed: false,
  toasts: [],
};

function toApiErrorSnapshot(envelope: CmtsErrorEnvelope): CmtsApiErrorSnapshot {
  return {
    kind: envelope.kind,
    status: envelope.status,
    message: envelope.message,
    code: envelope.code,
    correlationId: envelope.correlationId,
    retryable: envelope.retryable,
    atUtc: new Date().toISOString(),
  };
}

const uiSlice = createSlice({
  name: "ui",
  initialState,
  reducers: {
    globalLoadingChanged(state, action: PayloadAction<boolean>) {
      state.globalLoading = action.payload;
    },
    sidebarCollapsedChanged(state, action: PayloadAction<boolean>) {
      state.sidebarCollapsed = action.payload;
    },
    correlationRecorded(state, action: PayloadAction<string>) {
      state.lastCorrelationId = action.payload;
    },
    apiErrorRecorded(state, action: PayloadAction<CmtsErrorEnvelope>) {
      const snapshot = toApiErrorSnapshot(action.payload);
      state.lastError = snapshot;
      state.lastCorrelationId = snapshot.correlationId ?? state.lastCorrelationId;
      if (snapshot.status === 403) {
        state.permissionBlock = {
          message: snapshot.message,
          atUtc: snapshot.atUtc,
          correlationId: snapshot.correlationId,
        };
      }
    },
    apiErrorCleared(state) {
      state.lastError = undefined;
    },
    permissionBlocked(state, action: PayloadAction<{ permission?: string | undefined; route?: string | undefined; message: string; correlationId?: string | undefined }>) {
      state.permissionBlock = { ...action.payload, atUtc: new Date().toISOString() };
      state.lastCorrelationId = action.payload.correlationId ?? state.lastCorrelationId;
    },
    permissionBlockCleared(state) {
      state.permissionBlock = undefined;
    },
    toastShown(state, action: PayloadAction<{ id?: string | undefined; tone: ToastTone; message: string; correlationId?: string | undefined }>) {
      const id = action.payload.id ?? `toast-${Date.now().toString(36)}`;
      state.toasts = [...state.toasts, { ...action.payload, id, atUtc: new Date().toISOString() }].slice(-8);
    },
    toastDismissed(state, action: PayloadAction<string>) {
      state.toasts = state.toasts.filter((toast) => toast.id !== action.payload);
    },
    uiCleared(state) {
      state.globalLoading = false;
      state.sidebarCollapsed = false;
      state.lastCorrelationId = undefined;
      state.lastError = undefined;
      state.permissionBlock = undefined;
      state.toasts = [];
    },
  },
});

export const uiActions = uiSlice.actions;
export const uiReducer = uiSlice.reducer;
